// DO NOT EDIT -- generated from fs/api/search/search.proto

import * as protobuf from '../../../google/protobuf';
import * as segment from '../segment';

/**
 * Scope defines the different levels at which documents can be joined and queried.
 * Scope values follow the hierarchical structure of our documents (individuals have sessions, sessions have pages, pages have events, etc.).
 * WARNING: Scope numerical values cannot be compared directly! Their numerical values do not indicate granularity.
 */
export enum Scope {
  SCOPE_INVALID = 0,
  /** SCOPE_EVENT is the scope at which only exact matching events are considered. */
  SCOPE_EVENT = 1,
  /** SCOPE_PAGE_VIEW is the scope at which all documents for page views shared with the matching events are considered. */
  SCOPE_PAGE_VIEW = 5,
  /** SCOPE_PAGE is the scope at which all documents for pages shared with the matching events are considered. */
  SCOPE_PAGE = 2,
  /** SCOPE_SESSION is the scope at which all documents for sessions shared with the matching events are considered. */
  SCOPE_SESSION = 3,
  /** SCOPE_INDIVIDUAL is the scope at which all documents for individuals shared with the matching events are considered. */
  SCOPE_INDIVIDUAL = 4
}

/**
 * Search defines the language used to retrieve documents.
 * Each high level search type results in documents of the corresponding type.
 */
export interface Search {
  event?: EventSearch | null;
  session?: SessionSearch | null;
  individual?: IndividualSearch | null;
  pageview?: PageViewSearch | null;
}

/**
 * Search defines the language used to retrieve documents.
 * Each high level search type results in documents of the corresponding type.
 */
export namespace Search {
  export enum type {
    event = 1,
    session = 2,
    individual = 3,
    pageview = 4
  }

  export function type_oneof(m: Search): { opt: type.event, val: EventSearch } | { opt: type.individual, val: IndividualSearch } | { opt: type.pageview, val: PageViewSearch } | { opt: type.session, val: SessionSearch } | null {
    if (m.event != null) { return { opt: type.event, val: m.event } }
    if (m.session != null) { return { opt: type.session, val: m.session } }
    if (m.individual != null) { return { opt: type.individual, val: m.individual } }
    if (m.pageview != null) { return { opt: type.pageview, val: m.pageview } }
    return null;
  }
}

/** EventSearch defines the language used to retrieve event documents. */
export interface EventSearch {
  /**
   * The segment intersection for which events will be returned.
   * The resulting events matched will be those performed by individuals that are part of all of the provided segments.
   * If no segments are provided, all events will be matched.
   */
  for: segment.SegmentDefinition[];
  /**
   * Matching behaviors restricts events to those that match any of the provided behaviors widened to the provided scope.
   * The resulting events matched will be all of the events at the provided scope relative to the matched events.
   * For example, if Scope_SCOPE_SESSION is provided and 3 behaviors are matched across 2 sessions. All events for the 2
   * sessions that contain the 3 matched behaviors will be a part of the result set.
   * If no matching behaviors are provided, all events will be matched.
   */
  matchingBehaviors: EventSearch.MatchingBehaviors[];
  /**
   * Select filters exactly which events to produce on what platform in what time range.
   * Selecting does not affect the query. It only affects the resulting events produced (i.e. it's just a lens into
   * the results).
   * The resulting events will be contained by at least one select statement.
   * If no selects are provided, all events will be matched.
   */
  select: EventSearch.Select[];
}

/** EventSearch defines the language used to retrieve event documents. */
export namespace EventSearch {
  export interface MatchingBehaviors {
    /** The behaviors that must be exhibited to match. */
    behaviors: segment.Behavior[];
    /** The behaviors that must not be exhibited to match. */
    excludeBehaviors: segment.Behavior[];
    /** The constraints that apply to the behaviors. */
    constraints: segment.Constraints | null;
    /**
     * The constraints that apply to the exclude behaviors (if set).
     * If set:
     * - if in order is true, *all* excluded behaviors must be matched in order for the scoped behaviors to be excluded
     * - if in order is false (matches defaults below), at least one excluded behavior must be matched in order for the scoped behaviors to be
     * excluded If not set (the default) and exclude_behaviors is non empty, the following values are used:
     * - in order is false
     * - in same session matches constraints in same session
     * - platform is unset
     * - time range matches constraints time range
     */
    excludeConstraints: segment.Constraints | null;
    /**
     * The scope at which events should be extracted based on matched behaviors.
     * The scope is context specific to the matching positive behaviors.
     */
    widenToScope: Scope;
  }

  export interface Select {
    /**
     * The exact events to select out.
     * If empty, all events are selected.
     * 
     * @deprecated
     */
    events: segment.Event[];
    /**
     * The exact behhaviors to select out.
     * If empty, all behaviors are selected.
     * Note: Some behaviors are constituted by a collection of activity and will have mixed results at selection.
     */
    behaviors: segment.Behavior[];
    /**
     * The platform for which events should be selected out.
     * If not set, all platforms will be matched.
     */
    platform: segment.Platform;
    /**
     * The time range for which events should be selected out.
     * If not set, all time will be matched.
     */
    timeRange: segment.TimeRange | null;
    /**
     * Whether or not to restrict the events to those that are from completed sessions.
     * Additionally, modifies time_range to restrict using SessionStart rather than EventStart
     */
    completeSessions: boolean;
  }
}

export interface PageViewSearch {
  /**
   * The segment intersection for which page views will be returned.
   * The resulting page views matched will be those owned by individuals that are part of all of the provided segments.
   * If no segments are provided, all page views will be matched.
   */
  for: segment.SegmentDefinition[];
  /** With events restricts page views to those that contain events produced by all of the provided event searches. */
  withEvents: EventSearch[];
}

export interface SessionSearch {
  /**
   * The segment intersection for which sessions will be returned.
   * The resulting sessions matched will be those owned by individuals that are part of all of the provided segments.
   * If no segments are provided, all sessions will be matched.
   */
  for: segment.SegmentDefinition[];
  /** With events restricts sessions to those that contain events produced by all of the provided event searches. */
  withEvents: EventSearch[];
}

export interface IndividualSearch {
  /**
   * The segment intersection for which individuals will be returned.
   * The resulting individuals matched will be those that are part of all of the provided segments.
   * If no segments are provided, all individuals will be matched.
   */
  for: segment.SegmentDefinition[];
  /** With events restricts individuals to those that contain events produced by all of the provided event searches. */
  withEvents: EventSearch[];
}

export interface PageSearch {
  /**
   * The segment intersection for which pages will be returned.
   * The resulting pages matched will be those that are part of all of the provided segments.
   * If no segments are provided, all pages will be matched.
   */
  for: segment.SegmentDefinition[];
  /** With events restricts pages to those that contain events produced by all of the provided event searches. */
  withEvents: EventSearch[];
}

/**
 * CacheKey is an embeddable proto to be included in cache key messages for data that could affect
 * rendering and / or search results that otherwise do not have an effect on the request payload.
 */
export interface CacheKey {
  definedEventVersionsHash: protobuf.uint64;
  namedElementVersionsHash: protobuf.uint64;
  journeyMapVersionsHash: protobuf.uint64;
  privacyFilteredTimeRanges: string;
  specMappingVersionsHash: protobuf.uint64;
  specMappingPropertiesHash: protobuf.uint64;
}
